import { useQuery } from '@tanstack/react-query';

import { GetSubscriptionsInfoResponse } from '@/api/subscriptions/types';
import { RequestOptions, axios } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';

type GetSubscriptionInfoQueryParams = { status?: 'active' | 'past_due' | 'canceled' | 'all' | 'current' | 'latest' };
export const getSubscriptionInfo = async (
    { status = 'current' }: GetSubscriptionInfoQueryParams,
    options?: RequestOptions
) => {
    return axios.get<GetSubscriptionsInfoResponse, GetSubscriptionsInfoResponse>('/api/v2/subscriptions', {
        params: { status },
        ...options,
    });
};

type UseSubscriptionsInfoOptions = GetSubscriptionInfoQueryParams & {
    config?: QueryConfig<typeof getSubscriptionInfo>;
};

/**
 * Custom hook for fetching Stripe subscription information.
 *
 * @param options - Configuration options for the subscription query
 * @param options.config - Additional react-query configuration options
 * @param options.status - Status of of stripe subscription to be fetched (default: 'current').
 * 'current' - active and past_due subscriptions
 * 'latest' - returns 'active' if there is one, otherwise 'past_due', otherwise 'canceled'
 * @returns A react-query result object containing subscription information
 */
export const useSubscriptionsInfo = ({ config, ...params }: UseSubscriptionsInfoOptions = {}) => {
    return useQuery<GetSubscriptionsInfoResponse>({
        ...config,
        queryKey: ['stripe-subscription-info', params],
        queryFn: ({ signal }) => getSubscriptionInfo(params, { signal }),
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};
